import { Box } from '@material-ui/core'
import { EXCLUDE_DROP_MEDIA_FIELD_PROFILE, MEDIA_BLOCK_WIDTH_FOR_FIX_AUDIO_ITEM, MEDIA_ELEMENTS_SIZE } from 'constant'
import { MENU_ITEMS } from 'enums'
import React, { CSSProperties, FC, ReactNode } from 'react'
import { MediaAsset } from 'models/Asset'
import { VirtualRowItemData } from './types'

interface VirtualRowRendererProps {
  index: number,
  style: CSSProperties,
  data: VirtualRowItemData
}

export const Inner = React.forwardRef<HTMLDivElement, { children: ReactNode; style: CSSProperties }>(
  ({ children, style, ...rest }, ref) => {
    const childrenArray = React.Children.toArray(children)
    const firstChild = childrenArray.find(child => React.isValidElement(child)) as React.ReactElement
    const itemData = firstChild?.props?.data as VirtualRowItemData
    const {
      items,
      ItemComponent,
      countRowSourceAssets,
      handleItemMouseDown,
      handleItemClick,
      setUIElementBounds,
      menuItem,
      isItemsDraggable,
      before,
      after,
    } = itemData
    const showDropArea = !EXCLUDE_DROP_MEDIA_FIELD_PROFILE.includes(__APP_PROFILE__)
      && (menuItem === MENU_ITEMS.MEDIA || menuItem === MENU_ITEMS.PROJECT_MEDIA)
    const extraMediaElems = showDropArea ? [ {} as MediaAsset ] : []
    return (
      <>
        <div ref={ref} style={style} {...rest}>
          {/* First fixed row for media uploader */}
          {showDropArea && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: '3px',
                right: 0,
                width: style.width,
                minWidth: '100%',
                height: style.height,
                minHeight: '100vh',
                paddingLeft: '10px',
                display: 'flex',
              }}
            >
              {before}
              {/* [ {} as MediaAsset ] - empty space for before element */}
              {extraMediaElems.concat(items.slice(0, Math.max(0, countRowSourceAssets - 1))).map((item, i) => (
                <Box
                  style={{
                    width: i > 0 ? 'fit-content' : `${MEDIA_ELEMENTS_SIZE[menuItem].width}px`,
                    pointerEvents: i > 0 ? 'all' : 'none',
                  }}
                  key={`row0-${item.id}-${menuItem}`}
                >
                  {i === 0
                    ? null
                    : (
                      ItemComponent({
                        asset: item,
                        draggable: isItemsDraggable,
                        onItemMouseDown: handleItemMouseDown,
                        onItemClick: handleItemClick,
                        setUIElementBounds,
                      })
                    )}
                </Box>
              ))}
            </div>
          )}
          {/* Virtual rows */}
          {children}
        </div>
        {after && (
          <Box
            style={{
              width: '100%',
              flex: 1,
            }}
          >
            {after}
          </Box>
        )}
      </>
    )
  }
)

export const VirtualRowRenderer: FC<VirtualRowRendererProps> = React.memo(rowItem => {
  const { index, style, data } = rowItem
  const {
    items,
    ItemComponent,
    countRowSourceAssets,
    handleItemMouseDown,
    handleItemClick,
    setUIElementBounds,
    mediaColumnSize,
    menuItem,
    isItemsDraggable,
  } = data
  const isMedia = menuItem === MENU_ITEMS.MEDIA || menuItem === MENU_ITEMS.PROJECT_MEDIA
  const isExcludeDropArea = EXCLUDE_DROP_MEDIA_FIELD_PROFILE.includes(__APP_PROFILE__)
  const isFirstMediaRow = isMedia && !index
  const isFirstRow = !index

  const startItemIndex = isMedia && !isExcludeDropArea
    ? Math.max(0, index * countRowSourceAssets - 1)
    : index * countRowSourceAssets
  const end = startItemIndex + countRowSourceAssets
  const endItemIndex = Math.min(end, items.length)

  // because the Inner component wrapper has a non-virtual row
  if (isFirstMediaRow && !isExcludeDropArea) {
    return <div style={{ ...style, pointerEvents: 'none' }} />
  }

  return (
    <>
      <div
        style={{
          ...style,
          paddingLeft: '10px',
          display: 'flex',
          top: isFirstRow ? '3px' : style.top,
        }}
      >
        {items.slice(startItemIndex, endItemIndex).map((item, colIndex) => {
          const itemKey = `${menuItem}-${item.id}-${startItemIndex + colIndex}`
          return (
            <>
              <Box
                style={{
                  width: menuItem === MENU_ITEMS.AUDIO
                    && mediaColumnSize < MEDIA_BLOCK_WIDTH_FOR_FIX_AUDIO_ITEM
                    ? '100%'
                    : 'fit-content',
                }}
                key={itemKey}
              >
                {ItemComponent({
                  asset: item,
                  draggable: isItemsDraggable,
                  onItemMouseDown: handleItemMouseDown,
                  onItemClick: handleItemClick,
                  setUIElementBounds: (startItemIndex + colIndex) === 0 ? setUIElementBounds : null,
                })}
              </Box>
            </>
          )
        })}
      </div>
      {((startItemIndex + countRowSourceAssets) > items.length) && <div style={{ flexGrow: 1 }} />}
    </>
  )
})
